import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Subscription } from 'rxjs';
import {
  BuildingType,
  getHouseholdAdditionalCoverLabel,
  getHouseholdCoversLabel,
  getHouseholdLiabilityLabel,
  getHouseTypeLabel,
  getOwnershipConditionLabel,
  HouseholdAdditionalCovers,
  HouseholdCovers,
  HouseHoldLiability,
  HouseType,
  OwnershipConditionsEnum,
} from 'src/app/_enums/household.enum';
import { InsuranceType } from 'src/app/_enums/insurance-type.enum';
import { getBuildingTypeLabel } from 'src/app/_enums/rc-household/building-type.enum';
import { formatDateForInput } from 'src/app/_helpers/utils';
import { FamilyMember } from 'src/app/_types/auth.types';
import { HouseholdMetadata } from 'src/app/_types/lead.types';

import { LeadFormBase } from '../lead-form-base';

@Component({
  selector: 'app-lead-rc-household-form',
  templateUrl: './rc-household-form.component.html',
  styleUrls: ['./rc-household-form.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatFormFieldModule,
    MatSelectModule,
  ],
})
export class RcHouseholdFormComponent
  extends LeadFormBase
  implements OnDestroy, OnInit
{
  @Input()
  formGroup!: FormGroup;

  @Input()
  familyMembers!: FamilyMember[];

  private _startDate!: string;

  get startDate(): string {
    return this._startDate;
  }

  set startDate(value: string) {
    this._startDate = value;
    if (value)
      this.metadataFormGroup.controls['startDate'].setValue(
        new Date(value).toLocaleDateString('fr-FR')
      );
  }

  memberIdSubscription!: Subscription;

  hasDogSubscription!: Subscription;

  wasInsuredSubscription!: Subscription;

  constructor(private fb: FormBuilder) {
    super();
  }

  metadataFormGroup: FormGroup = this.fb.group({
    type: this.fb.control(InsuranceType.RC_HOUSEHOLD),
    ownershipConditions: this.fb.control(null),
    startDate: this.fb.control(null),
    adultCount: this.fb.control(null),
    childrenCount: this.fb.control(null),
    hasDog: this.fb.control(null),
    dogCount: this.fb.control(null),
    placeType: this.fb.group({
      houseType: this.fb.control(null),
      buildingType: this.fb.control(null),
      amountToCover: this.fb.control(null),
      address: this.fb.control(null),
      postalCode: this.fb.control(null),
    }),
    rooms: this.fb.control(null),
    covers: this.fb.control(null),
    civilLiability: this.fb.control(null),
    amountToCover: this.fb.control(null),
    deductible: this.fb.control(null),
    additionalCovers: this.fb.control(null),
    birthDate: this.fb.control(null),
    wasInsured: this.fb.control(null),
    oldCompany: this.fb.control(null),
    policyNumber: this.fb.control(null),
    sufferedClaims: this.fb.control(null),
    propositionRefused: this.fb.control(null),
  });

  setMemberInfo(familyMemberId: string) {
    if (!this.familyMembers || this.familyMembers.length === 0) return;
    const familyMember = this.familyMembers.find(
      (x) => x.familyMemberId === familyMemberId
    );
    this.metadataFormGroup.controls['birthDate'].setValue(
      new Date(familyMember?.dob!).toLocaleDateString('fr-FR')
    );
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.formGroup.removeControl('metadata');
    if (this.metadata) {
      const rcMetadata = this.metadata as HouseholdMetadata;
      this.startDate = formatDateForInput(rcMetadata.startDate);
      this.metadataFormGroup.patchValue(this.metadata);
    }
    this.formGroup.addControl('metadata', this.metadataFormGroup);

    if (this.formGroup.controls['memberId'].value) {
      this.setMemberInfo(this.formGroup.controls['memberId'].value);
    }

    this.memberIdSubscription = this.formGroup.controls[
      'memberId'
    ].valueChanges.subscribe((value) => {
      this.setMemberInfo(value);
    });

    this.hasDogSubscription = this.metadataFormGroup.controls[
      'hasDog'
    ].valueChanges.subscribe((value) => {
      if (!value) {
        this.metadataFormGroup.controls['dogCount'].reset();
      }
    });

    this.wasInsuredSubscription = this.metadataFormGroup.controls[
      'wasInsured'
    ].valueChanges.subscribe((value) => {
      if (!value) {
        this.metadataFormGroup.controls['oldCompany'].reset();
        this.metadataFormGroup.controls['policyNumber'].reset();
      }
    });
  }

  ownershipConditions = Object.values(OwnershipConditionsEnum);

  getOwnershipConditionLabel = getOwnershipConditionLabel;

  houseTypes = Object.values(HouseType);

  getHouseTypeLabel = getHouseTypeLabel;

  buildingTypes = Object.values(BuildingType);

  getBuildingTypeLabel = getBuildingTypeLabel;

  covers = Object.values(HouseholdCovers);

  getCoverLabel = getHouseholdCoversLabel;

  civilLiabilityValues = Object.values(HouseHoldLiability);

  getCivilLiabilityLabel = getHouseholdLiabilityLabel;

  additionalCovers = Object.values(HouseholdAdditionalCovers);

  getAdditionalCoverLabel = getHouseholdAdditionalCoverLabel;

  ngOnDestroy(): void {
    this.memberIdSubscription.unsubscribe();
    this.hasDogSubscription.unsubscribe();
    this.wasInsuredSubscription.unsubscribe();
  }

  getControl(control: AbstractControl): FormControl {
    return control as FormControl;
  }
}
